import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import GuideVideo from "../components/GuideVideo";
import {
    SITE_URL,
    organizationJsonLd,
    makeBreadcrumbJsonLd,
    makeVideoJsonLd,
} from "../seo/structuredData";
import "./About.css";

import event from "../assets/event.jpg";
import InnovationImage from "../assets/briksupport.jpg";
import nahBro from "../assets/nahbrik.JPG";

const founderJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE_URL}/about#founder`,
    name: "Asray Gopa",
    jobTitle: "Founder",
    alumniOf: {
        "@type": "CollegeOrUniversity",
        name: "Iowa State University",
    },
    worksFor: {
        "@type": "Organization",
        name: "The Brik",
        url: SITE_URL,
    },
    email: "asray@the-brik.com",
};

const storyVideoJsonLd = makeVideoJsonLd({
    name: "The Story Behind The Brik",
    description:
        "Founder Asray Gopa tells the story of how a lost dorm key at Iowa State became The Brik wallet.",
    poster: "/videos/brik-founder-story-poster.jpg",
    src: "/videos/brik-founder-story.mp4",
    duration: "PT57S",
});

const AboutUs = () => {
    return (
        <div>
            <SEO
                title="About The Brik: Founder Story from Iowa State"
                description="The Brik Wallet started with a lost dorm key and a $160 fee at Iowa State. Founder Asray Gopa built a metal wallet that keeps cards, ID, cash, keys, and a tracker together."
                path="/about"
                jsonLd={[
                    organizationJsonLd,
                    founderJsonLd,
                    storyVideoJsonLd,
                    makeBreadcrumbJsonLd([
                        { name: "Home", path: "/" },
                        { name: "About", path: "/about" },
                    ]),
                ]}
            />
            <Header />

            {/* Section 0: What The Brik is */}
            <section className="about-section2 section-normal">
                <div className="about-text green-background2">
                    <h1>About The Brik: Started by a Lost Dorm Key</h1>
                    <p>
                        The Brik is a metal wallet that keeps your cards, ID, cash,
                        keys, and a rechargeable tracking card together in one
                        compact everyday carry. It holds 1 ID in a quick-access
                        front slot and 7-8 cards in an RFID-protected compartment,
                        attaches your keys to an integrated keyring, and can be
                        found from your phone when it goes missing. It's machined
                        from anodized aluminum and sells for $69.99.
                    </p>
                </div>
                <div className="about-image2">
                    <img src={event} alt="The Brik team showing the metal wallet at an event" loading="lazy" />
                </div>
            </section>

            {/* Section 1: The origin story */}
            <section className="about-section2 section-reverse">
                <div className="about-image2">
                    <img src={InnovationImage} alt="Asray Gopa, founder of The Brik, with the wallet" loading="lazy" />
                </div>
                <div className="about-text green-background2">
                    <h2>The $160 dorm key</h2>
                    <p>
                        The Brik started at Iowa State University, where founder
                        Asray Gopa lost his dorm key and got charged $160 for the
                        replacement. The problem wasn't carelessness, it was
                        carrying three separate things: a wallet somewhere in a
                        backpack, an ID loose in a phone case, and keys on a bare
                        lanyard.
                    </p>
                    <p>
                        The first prototype was cut from cardboard in his dorm
                        room: one place for the ID, the cards, the cash, and the
                        key that had just cost him $160. More than 900 people
                        bought the original plastic Brik that grew out of that
                        prototype.
                    </p>
                </div>
            </section>

            {/* Section 2: The metal Brik today */}
            <section className="about-section2 section-normal">
                <div className="about-text green-background2">
                    <h2>Four years later: the Metal Brik</h2>
                    <p>
                        Today's Brik keeps the same layout that worked in
                        cardboard, machined from solid anodized aluminum: ID in
                        front, cards in the middle, cash in the back, keys on the
                        integrated keyring. And because the original problem was
                        losing things, every Metal Brik now ships with a
                        rechargeable tracking card, so the wallet itself can be
                        rung and located from your phone.
                    </p>
                    <p>
                        Asray graduated from Iowa State with a degree in Computer
                        Science and runs The Brik from Ames, Iowa, alongside other
                        startup ventures, hackathons, and entrepreneurial
                        competitions.
                    </p>
                </div>
                <div className="about-image2">
                    <img src={nahBro} alt="Asray Gopa at Iowa State University" loading="lazy" />
                </div>
            </section>

            <GuideVideo
                heading="Story time, from the founder"
                caption="Asray tells the whole thing on camera: the lost key, the fee, and the wallet that came out of it."
                src="/videos/brik-founder-story.mp4"
                poster="/videos/brik-founder-story-poster.jpg"
            />

            <Footer />
        </div>
    );
};

export default AboutUs;
